import { AgChartsCommunityModule } from "ag-charts-community";
import {
  AllEnterpriseModule,
  ClientSideRowModelModule,
  LicenseManager,
  ModuleRegistry,
  SparklinesModule
} from "ag-grid-enterprise";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { App } from "./App";

/**
 * This allows us to enable mocking in production for demos or troubleshooting
 * by setting localStorage.msw = 'on' in the console. It also allows developers
 * to force it off in dev if needed.
 *
 * dev: ON by default unless forced OFF
 * prod: OFF by default unless forced ON
 *
 * How to force:
 *   - in console: localStorage.msw = 'on' | 'off' (reload required)
 *   - to reset: localStorage.removeItem('msw')
 */

function shouldStartMSW(): boolean {
  const dev = import.meta.env.DEV;

  // Persistent toggle via localStorage.msw = "on" | "off"
  const persisted = (localStorage.getItem("msw") || "").trim().toLowerCase();
  if (persisted === "on") {
    return true;
  }
  if (persisted === "off") {
    return false;
  }

  // Default behavior
  return dev;
}

async function enableMocking() {
  if (!shouldStartMSW()) {
    return;
  }

  const { worker } = await import("./mocks/browser");
  await worker.start({
    onUnhandledRequest: import.meta.env.DEV ? "bypass" : "error",
    serviceWorker: { url: `${import.meta.env.BASE_URL}mockServiceWorker.js` }
  });
}

/*
AG Grid setup
*/
ModuleRegistry.registerModules([
  ClientSideRowModelModule,
  AllEnterpriseModule.with(AgChartsCommunityModule),
  SparklinesModule.with(AgChartsCommunityModule)
]);

LicenseManager.setLicenseKey(import.meta.env.VITE_AG_GRID_KEY || "");

enableMocking().finally(() => {
  createRoot(document.getElementById("root")!).render(
    <StrictMode>
      <App />
    </StrictMode>
  );
});
